import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useContent } from "@/context/ContentContext";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/whats-included", label: "What's Included" },
  { to: "/events", label: "Events" },
  { to: "/gallery", label: "Gallery" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { content } = useContent();
  const galleryHidden = content?.settings?.gallery_enabled === false;
  const links = LINKS.filter((l) => !(l.to === "/gallery" && galleryHidden));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header className="fixed top-0 inset-x-0 z-50 py-3 sm:py-4" data-testid="navbar">
      <div className="mx-auto max-w-7xl px-4">
        <nav className={`hidden lg:grid grid-cols-[1fr_auto_1fr] items-center rounded-full px-4 h-20 transition-all duration-300 ${scrolled ? "glass-dark shadow-[0_10px_40px_rgba(0,0,0,0.5)]" : "glass-dark"}`}>
          <Link to="/" className="flex items-center" data-testid="nav-home-logo"><img src="/brand/logo-badge.png" alt="Keys Event Center" className="h-16 w-auto object-contain" data-testid="site-logo" /></Link>
          <div className="flex items-center justify-center gap-1">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={({ isActive }) => `relative px-3.5 py-2 text-sm font-semibold rounded-full transition-colors duration-200 ${isActive ? "text-glow-cyan" : "text-white/70 hover:text-white"}`}>
                {({ isActive }) => (<>{l.label}{isActive && <span className="absolute left-1/2 -translate-x-1/2 -bottom-0.5 h-[3px] w-5 rounded-full bg-[#2fd0c6] shadow-[0_0_10px_#2fd0c6]" />}</>)}
              </NavLink>
            ))}
          </div>
          <div className="flex justify-end">
            <Link to="/reservations" data-testid="nav-reserve-btn" className="inline-flex items-center rounded-full bg-[#ff5147] text-white font-mono-arcade font-bold uppercase tracking-wider text-sm px-6 py-2.5 border border-white/25 shadow-[0_6px_18px_rgba(255,64,56,0.35)] hover:brightness-110 hover:-translate-y-0.5 transition duration-200">Reserve Your Party</Link>
          </div>
        </nav>

        <div className="lg:hidden flex items-center justify-between">
          <Link to="/" data-testid="nav-home-logo-mobile" className="flex items-center"><img src="/brand/logo-badge.png" alt="Keys Event Center" className="h-14 w-auto object-contain" /></Link>
          <button onClick={() => setOpen((o) => !o)} aria-label="Toggle menu" data-testid="nav-mobile-toggle"
            className="h-11 w-11 grid place-items-center rounded-full glass-dark text-white">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden mt-2 rounded-3xl glass-dark shadow-xl p-3 flex flex-col gap-1" data-testid="mobile-menu">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} className={({ isActive }) => `px-4 py-3 rounded-2xl text-base font-semibold ${isActive ? "bg-white/10 text-glow-cyan" : "text-white/80"}`}>{l.label}</NavLink>
            ))}
            <Link to="/reservations" className="btn-primary text-center px-5 py-3 mt-1" data-testid="mobile-reserve-btn">Reserve Your Party</Link>
          </div>
        )}
      </div>
    </header>
  );
}
